import { inject, Injectable, InjectionToken, Provider } from '@angular/core';
import {
  defer,
  finalize,
  from,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';

/** Keshni qayerda saqlash. */
export type CacheStorage = 'memory' | 'local' | 'session' | 'indexeddb';

export interface CacheSetOptions {
  /** Qayerda saqlash (default: config.defaultStorage yoki 'memory'). */
  storage?: CacheStorage;
  /**
   * Yashash muddati, ms.
   *  - berilmasa: config.defaultTtl (default 5 daqiqa)
   *  - 0 yoki Infinity: muddatsiz (faqat qo'lda yoki clear() bilan o'chadi)
   */
  ttl?: number;
}

export interface CacheServiceConfig {
  /** Barcha kalitlar oldiga qo'shiladigan prefiks. Default: 'olymp:cache:'. */
  keyPrefix?: string;
  /** Default saqlash joyi. Default: 'memory'. */
  defaultStorage?: CacheStorage;
  /** Default TTL, ms. Default: 5 daqiqa. */
  defaultTtl?: number;
  /**
   * Kesh versiyasi (odatda `environment` dan). Ilova yuklanganda saqlangan
   * versiya bilan solishtiriladi — farq bo'lsa BARCHA kesh (barcha storage,
   * barcha userlar keyingi kirishida) tozalanadi. Deploy paytida bu qiymatni
   * o'zgartirish = hamma foydalanuvchining eski keshini majburan yangilash.
   */
  version?: string;
}

export const CACHE_CONFIG = new InjectionToken<CacheServiceConfig>(
  'CACHE_CONFIG',
);

export function provideCache(config: CacheServiceConfig = {}): Provider {
  return { provide: CACHE_CONFIG, useValue: config };
}

interface CacheEnvelope<T> {
  /** qiymat */
  v: T;
  /** expiresAt (epoch ms), 0 = muddatsiz */
  e: number;
  /** yozilgan vaqt (epoch ms) — debug uchun */
  s: number;
  /** kesh versiyasi — config.version bilan mos kelmasa yozuv yaroqsiz */
  n?: string;
}

/* -------------------------------------------------------------------------- */
/*  Store'lar (barchasi async — IndexedDB uchun)                              */
/* -------------------------------------------------------------------------- */

interface CacheStore {
  get(key: string): Promise<CacheEnvelope<unknown> | null>;
  set(key: string, value: CacheEnvelope<unknown>): Promise<void>;
  remove(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

class MemoryStore implements CacheStore {
  private readonly map = new Map<string, CacheEnvelope<unknown>>();
  /** RAM cheksiz o'smasligi uchun — eng eski yozuv birinchi bo'lib o'chadi (LRU-ish). */
  private readonly maxEntries = 200;

  async get(key: string) {
    return this.map.get(key) ?? null;
  }
  async set(key: string, value: CacheEnvelope<unknown>) {
    this.map.delete(key);
    this.map.set(key, value);
    if (this.map.size > this.maxEntries) {
      const oldest = this.map.keys().next().value;
      if (oldest !== undefined) {
        this.map.delete(oldest);
      }
    }
  }
  async remove(key: string) {
    this.map.delete(key);
  }
  async keys() {
    return [...this.map.keys()];
  }
}

class WebStorageStore implements CacheStore {
  constructor(private readonly backing: () => Storage | null) {}

  private get storage(): Storage | null {
    try {
      return this.backing();
    } catch {
      return null;
    }
  }

  async get(key: string) {
    const raw = this.storage?.getItem(key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as CacheEnvelope<unknown>;
    } catch {
      this.storage?.removeItem(key);
      return null;
    }
  }
  async set(key: string, value: CacheEnvelope<unknown>) {
    try {
      this.storage?.setItem(key, JSON.stringify(value));
    } catch {
      /* kvota to'ldi / private mode — jim o'tkazamiz */
    }
  }
  async remove(key: string) {
    this.storage?.removeItem(key);
  }
  async keys() {
    const s = this.storage;
    if (!s) {
      return [];
    }
    const out: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k) {
        out.push(k);
      }
    }
    return out;
  }
}

class IndexedDbStore implements CacheStore {
  private readonly dbName = 'olymp-cache';
  private readonly storeName = 'entries';
  private dbPromise?: Promise<IDBDatabase | null>;

  private open(): Promise<IDBDatabase | null> {
    if (this.dbPromise) {
      return this.dbPromise;
    }
    this.dbPromise = new Promise((resolve) => {
      if (typeof indexedDB === 'undefined') {
        resolve(null);
        return;
      }
      const req = indexedDB.open(this.dbName, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(this.storeName);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
    return this.dbPromise;
  }

  private async tx<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest,
  ): Promise<T | undefined> {
    const db = await this.open();
    if (!db) {
      return undefined;
    }
    return new Promise<T | undefined>((resolve) => {
      const store = db
        .transaction(this.storeName, mode)
        .objectStore(this.storeName);
      const request = run(store);
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(undefined);
    });
  }

  async get(key: string) {
    const res = await this.tx<CacheEnvelope<unknown>>('readonly', (s) =>
      s.get(key),
    );
    return res ?? null;
  }
  async set(key: string, value: CacheEnvelope<unknown>) {
    await this.tx('readwrite', (s) => s.put(value, key));
  }
  async remove(key: string) {
    await this.tx('readwrite', (s) => s.delete(key));
  }
  async keys() {
    const res = await this.tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
    return (res ?? []).map(String);
  }
}

/* -------------------------------------------------------------------------- */
/*  Service                                                                    */
/* -------------------------------------------------------------------------- */

@Injectable({ providedIn: 'root' })
export class CacheService {
  private readonly config = inject(CACHE_CONFIG, { optional: true }) ?? {};

  private readonly prefix = this.config.keyPrefix ?? 'olymp:cache:';
  private readonly defaultStorage: CacheStorage =
    this.config.defaultStorage ?? 'memory';
  private readonly defaultTtl = this.config.defaultTtl ?? 5 * 60 * 1000;
  private readonly version = this.config.version ?? '';

  private readonly stores: Record<CacheStorage, CacheStore> = {
    memory: new MemoryStore(),
    local: new WebStorageStore(() => globalThis.localStorage ?? null),
    session: new WebStorageStore(() => globalThis.sessionStorage ?? null),
    indexeddb: new IndexedDbStore(),
  };

  /** wrap() uchun in-flight dedup — bir xil kalitni bir vaqtda so'raganlar birlashadi. */
  private readonly inFlight = new Map<string, Observable<unknown>>();

  constructor() {
    this.enforceVersion();
  }

  /**
   * Saqlangan kesh versiyasini config.version bilan solishtiradi.
   * Farq bo'lsa — barcha storage'dagi kesh tozalanadi va yangi versiya yoziladi.
   * (Deploy'da environment.cacheVersion o'zgarsa — hamma userda ishga tushadi.)
   */
  private enforceVersion(): void {
    if (!this.version) {
      return;
    }
    const markerKey = `${this.prefix}__version`;
    let stored: string | null = null;
    try {
      stored = globalThis.localStorage?.getItem(markerKey) ?? null;
    } catch {
      /* localStorage yo'q — o'tkazamiz */
    }
    if (stored === this.version) {
      return;
    }
    void this.clear().finally(() => {
      try {
        globalThis.localStorage?.setItem(markerKey, this.version);
      } catch {
        /* ignore */
      }
    });
  }

  /** Kalit bo'yicha qiymat. Muddati o'tgan bo'lsa — o'chiradi va undefined qaytaradi. */
  async get<T>(key: string, storage?: CacheStorage): Promise<T | undefined> {
    const store = this.stores[storage ?? this.defaultStorage];
    const env = (await store.get(this.k(key))) as CacheEnvelope<T> | null;
    if (!env) {
      return undefined;
    }
    const expired = env.e && env.e <= Date.now();
    const staleVersion = this.version && (env.n ?? '') !== this.version;
    if (expired || staleVersion) {
      await store.remove(this.k(key));
      return undefined;
    }
    return env.v;
  }

  async set<T>(
    key: string,
    value: T,
    options: CacheSetOptions = {},
  ): Promise<void> {
    const store = this.stores[options.storage ?? this.defaultStorage];
    const ttl = options.ttl ?? this.defaultTtl;
    const expiresAt =
      !ttl || ttl === Infinity ? 0 : Date.now() + Math.max(0, ttl);
    const env: CacheEnvelope<T> = {
      v: value,
      e: expiresAt,
      s: Date.now(),
      n: this.version || undefined,
    };
    await store.set(this.k(key), env);
  }

  async has(key: string, storage?: CacheStorage): Promise<boolean> {
    return (await this.get(key, storage)) !== undefined;
  }

  async remove(key: string, storage?: CacheStorage): Promise<void> {
    const targets: CacheStorage[] = storage
      ? [storage]
      : (Object.keys(this.stores) as CacheStorage[]);
    await Promise.all(targets.map((t) => this.stores[t].remove(this.k(key))));
  }

  /** Bitta store'ni yoki barchasini tozalaydi (faqat shu service yozgan kalitlar). */
  async clear(storage?: CacheStorage): Promise<void> {
    const targets: CacheStorage[] = storage
      ? [storage]
      : (Object.keys(this.stores) as CacheStorage[]);
    await Promise.all(
      targets.map(async (t) => {
        const store = this.stores[t];
        const keys = await store.keys();
        await Promise.all(
          keys
            .filter(
              (key) =>
                key.startsWith(this.prefix) &&
                !key.startsWith(`${this.prefix}__`),
            )
            .map((key) => store.remove(key)),
        );
      }),
    );
  }

  /** Shu service yozgan, `predicate` mos kelgan yozuvlarni o'chiradi. */
  async removeWhere(
    storage: CacheStorage,
    predicate: (key: string) => boolean,
  ): Promise<void> {
    const store = this.stores[storage];
    const keys = (await store.keys()).filter(
      (key) => key.startsWith(this.prefix) && predicate(key),
    );
    await Promise.all(keys.map((key) => store.remove(key)));
  }

  /**
   * `keyPrefix` bilan boshlanadigan yozuvlardan muddati o'tganlarini o'chiradi va
   * `maxEntries` dan oshganini eng eski yozilganidan boshlab tozalaydi.
   */
  async prune(
    storage: CacheStorage,
    keyPrefix: string,
    maxEntries: number,
  ): Promise<void> {
    const store = this.stores[storage];
    const fullPrefix = this.k(keyPrefix);
    const keys = (await store.keys()).filter((key) =>
      key.startsWith(fullPrefix),
    );
    if (keys.length <= maxEntries) {
      return;
    }

    const now = Date.now();
    const alive: { key: string; savedAt: number }[] = [];
    for (const key of keys) {
      const env = await store.get(key);
      if (!env || (env.e && env.e <= now)) {
        await store.remove(key);
      } else {
        alive.push({ key, savedAt: env.s });
      }
    }

    alive.sort((a, b) => a.savedAt - b.savedAt);
    const excess = alive.length - maxEntries;
    for (let i = 0; i < excess; i++) {
      await store.remove(alive[i].key);
    }
  }

  /**
   * Cache-aside helper: keshda bo'lsa — o'shani, bo'lmasa `factory()` ni chaqirib
   * natijani keshlaydi. Bir vaqtda ketgan bir xil kalitli chaqiruvlar dedup qilinadi.
   *
   *   this.cache.wrap(
   *     'directory:regions',
   *     () => this.http.post<Region[]>(url, { rows: 1000 }),
   *     { storage: 'session', ttl: 10 * 60_000 },
   *   ).subscribe(...)
   */
  wrap<T>(
    key: string,
    factory: () => Observable<T>,
    options: CacheSetOptions = {},
  ): Observable<T> {
    const existing = this.inFlight.get(key) as Observable<T> | undefined;
    if (existing) {
      return existing;
    }

    const stream = defer(() => from(this.get<T>(key, options.storage))).pipe(
      switchMap((cached) => {
        if (cached !== undefined) {
          return of(cached);
        }
        return factory().pipe(
          switchMap((value) =>
            from(this.set(key, value, options)).pipe(
              switchMap(() => of(value)),
            ),
          ),
        );
      }),
      finalize(() => this.inFlight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.inFlight.set(key, stream);
    return stream;
  }

  private k(key: string): string {
    return key.startsWith(this.prefix) ? key : this.prefix + key;
  }
}
